import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@medikei/ui';
import {
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  FileText,
  Clock,
  ExternalLink,
  Copy,
  Check,
  RefreshCw,
  ShieldCheck,
  X,
  Calendar,
} from 'lucide-react';
import { medicalRecordsApi } from '@/api';
import type { Certificate } from '@/api/http-client';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { ErrorRetry } from '@/components/ui/ErrorRetry';
import { MeMedStatusBadge } from '@/components/memed/MeMedStatusBadge';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';

interface CertificateUI extends Certificate {
  doctorName: string;
  specialty: string;
  crm?: string;
  diagnosis: string;
  totalDays: number;
  memedId?: string;
  memedStatus?: 'PENDING' | 'SYNCED' | 'FAILED' | 'SIGNED' | 'CANCELLED';
  pdfUrl?: string;
}

interface CertificatesResponse {
  certificates: CertificateUI[];
  total: number;
  page: number;
  pageSize: number;
}

export function CertificatesPage() {
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const { data, isLoading, isError, isFetching, refetch } = useQuery<CertificatesResponse, Error>({
    queryKey: ['patient', 'certificates', page],
    queryFn: async () => {
      const response = await medicalRecordsApi.getPatientCertificates(page, 10);
      const list = Array.isArray(response?.data) ? response.data : [];
      return {
        certificates: list.map((cert: any) => {
          let calculatedDays = cert.daysOff;
          if (!calculatedDays && cert.startDate && cert.endDate) {
            const start = new Date(cert.startDate).getTime();
            const end = new Date(cert.endDate).getTime();
            calculatedDays = Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1);
          }

          return {
            ...cert,
            doctorName: cert.doctorName || cert.doctorId || 'Médico Responsável',
            specialty: cert.doctorSpecialty || cert.specialty || 'Clínica Geral',
            crm: cert.doctorCrm || '',
            diagnosis: cert.reason || cert.diagnosis || '',
            totalDays: calculatedDays || 1,
            observations: cert.observations || null,
            memedId: cert.memedCertificateId || cert.memedId,
            memedStatus: cert.memedStatus,
            pdfUrl: cert.pdfUrl || cert.memedPdfUrl || undefined,
          };
        }),
        total: response?.meta?.total ?? list.length,
        page: response?.meta?.page ?? page,
        pageSize: response?.meta?.limit ?? 10,
      };
    },
  });

  const handleCopyCertificate = async (cert: CertificateUI) => {
    const textToCopy = `*Atestado Médico - Medikei*
Médico: ${cert.doctorName} (${cert.specialty}${cert.crm ? ` • ${cert.crm}` : ''})
Período de Afastamento: ${formatDate(cert.startDate)} até ${formatDate(
      cert.endDate || cert.startDate
    )} (${cert.totalDays} ${cert.totalDays === 1 ? 'dia' : 'dias'})
${cert.diagnosis ? `Motivo / CID: ${cert.diagnosis}\n` : ''}${
      cert.observations ? `Observações: ${cert.observations}\n` : ''
    }Data de Emissão: ${formatDate(cert.createdAt)}
Validação Digital ICP-Brasil:
${cert.pdfUrl || 'Aguardando validação digital na MeMed'}

ID do Atestado: ${cert.id}`;

    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopiedId(cert.id);
      setTimeout(() => setCopiedId(null), 2500);
    } catch {
      setCopiedId(cert.id);
      setTimeout(() => setCopiedId(null), 2500);
    }
  };

  const handleViewPdf = (certificate: CertificateUI) => {
    if (certificate.pdfUrl) {
      window.open(certificate.pdfUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    toast({
      title: 'Assinatura Pendente',
      description: 'Este atestado ainda está aguardando a assinatura digital oficial na MeMed',
    });
  };

  const handleDownloadPdf = (certificate: CertificateUI) => {
    if (!certificate.pdfUrl) {
      toast({
        title: 'Assinatura Pendente',
        description: 'Este atestado ainda está aguardando a assinatura digital oficial na MeMed',
      });
      return;
    }

    const link = document.createElement('a');
    link.href = certificate.pdfUrl;
    link.target = '_blank';
    link.rel = 'noopener,noreferrer';
    link.download = `atestado_${certificate.id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredCertificates = (data?.certificates || []).filter((c) => {
    const term = search.toLowerCase().trim();
    if (!term) return true;
    return (
      c.doctorName.toLowerCase().includes(term) ||
      c.specialty.toLowerCase().includes(term) ||
      (c.crm && c.crm.toLowerCase().includes(term)) ||
      c.diagnosis.toLowerCase().includes(term) ||
      (c.observations && c.observations.toLowerCase().includes(term))
    );
  });

  const formatDate = (dateStr?: string | Date | null) => {
    if (!dateStr) return '--';
    try {
      const datePart = typeof dateStr === 'string' ? dateStr.split('T')[0] : '';
      if (datePart && datePart.includes('-')) {
        const [year, month, day] = datePart.split('-');
        if (day && month && year) return `${day}/${month}/${year}`;
      }
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return '--';
    }
  };

  const totalPages = data && data.pageSize ? Math.ceil(data.total / data.pageSize) : 1;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header com Estatística e Ação de Atualizar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
              Meus Atestados
            </h1>
            {data && data.total > 0 && (
              <Badge
                variant="outline"
                className="font-bold text-xs bg-primary/10 text-primary border-primary/20"
              >
                {data.total}
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground text-xs sm:text-sm mt-1">
            Acesse, visualize e faça download dos seus atestados médicos digitais emitidos
          </p>
        </div>

        <Button
          variant="outline"
          onClick={() => refetch()}
          disabled={isFetching}
          className="gap-2 rounded-xl font-semibold shadow-xs hover:bg-muted w-full sm:w-auto h-11"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin text-primary' : ''}`} />
          <span>Atualizar</span>
        </Button>
      </div>

      {/* Barra de Busca com Limpeza Rápida */}
      <div className="relative max-w-md">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Buscar por médico, diagnóstico ou motivo..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-input bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all shadow-xs"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground rounded-lg transition-colors"
            aria-label="Limpar busca"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Estados de Carregamento, Erro ou Vazio */}
      {isLoading ? (
        <LoadingOverlay message="Carregando atestados..." />
      ) : isError ? (
        <ErrorRetry message="Erro ao carregar atestados" onRetry={() => refetch()} />
      ) : filteredCertificates.length === 0 ? (
        <Card className="rounded-2xl border border-dashed border-border p-8 sm:p-12 text-center bg-card">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center mx-auto mb-4 shadow-xs">
            <FileText className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-bold text-foreground mb-1">
            {search ? 'Nenhum atestado encontrado' : 'Nenhum atestado encontrado'}
          </h3>
          <p className="text-xs sm:text-sm text-muted-foreground max-w-md mx-auto mb-6">
            {search
              ? `Não encontramos nenhum atestado correspondente a "${search}". Tente buscar por outro termo.`
              : 'Seus atestados médicos emitidos em consultas presenciais ou telemedicina aparecerão aqui com assinatura digital válida em todo o Brasil.'}
          </p>
          {search ? (
            <Button
              variant="outline"
              onClick={() => setSearch('')}
              className="rounded-xl font-semibold"
            >
              Limpar busca
            </Button>
          ) : (
            <Button
              onClick={() => refetch()}
              className="rounded-xl font-bold gap-2 shadow-md shadow-primary/20"
            >
              <RefreshCw className="w-4 h-4" />
              Verificar Novos Atestados
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredCertificates.map((certificate) => {
            const isMemedConfirmed =
              certificate.memedStatus === 'SIGNED' ||
              (!certificate.memedStatus && Boolean(certificate.pdfUrl));
            const hasOfficialPdf = Boolean(isMemedConfirmed && certificate.pdfUrl);
            const isCopied = copiedId === certificate.id;

            return (
              <Card
                key={certificate.id}
                className="rounded-2xl border border-border bg-card shadow-xs hover:shadow-md hover:border-primary/40 transition-all overflow-hidden"
              >
                <CardContent className="p-5 sm:p-6">
                  {/* Cabeçalho do Card */}
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-5 pb-4 border-b border-border/60">
                    <div className="flex items-start gap-3.5">
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500/20 via-amber-500/10 to-amber-500/5 border border-amber-500/25 flex items-center justify-center text-amber-600 dark:text-amber-400 shrink-0 shadow-xs">
                        <FileText className="h-6 w-6" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-bold text-lg text-foreground tracking-tight truncate">
                            {certificate.doctorName}
                          </h3>
                          {certificate.crm && (
                            <Badge
                              variant="outline"
                              className="text-[11px] font-semibold text-muted-foreground border-border/80"
                            >
                              {certificate.crm}
                            </Badge>
                          )}
                        </div>

                        <p className="text-sm font-semibold text-primary mt-0.5">
                          {certificate.specialty}
                        </p>

                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/40 px-2.5 py-1 rounded-lg border border-border/40">
                            <Calendar className="h-3.5 w-3.5 text-primary" />
                            Emitido em {formatDate(certificate.createdAt)}
                          </span>

                          {isMemedConfirmed ? (
                            <Badge
                              variant="outline"
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
                            >
                              <ShieldCheck className="h-3.5 w-3.5" />
                              Assinado Digitalmente (ICP-Brasil)
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
                            >
                              <Clock className="h-3.5 w-3.5" />
                              Pendente de Assinatura Digital
                            </Badge>
                          )}

                          {(certificate.memedStatus || certificate.memedId) && (
                            <MeMedStatusBadge
                              status={certificate.memedStatus || 'PENDING'}
                              memedId={certificate.memedId}
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Ações do Atestado */}
                    <div className="flex items-center gap-2 self-end sm:self-start shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyCertificate(certificate)}
                        className="rounded-xl text-xs font-semibold gap-1.5 h-9 hover:bg-muted"
                        title="Copiar dados do atestado"
                      >
                        {isCopied ? (
                          <>
                            <Check className="h-3.5 w-3.5 text-emerald-600" />
                            <span className="text-emerald-600 font-bold">Copiado</span>
                          </>
                        ) : (
                          <>
                            <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                            <span>Copiar</span>
                          </>
                        )}
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewPdf(certificate)}
                        className="rounded-xl text-xs font-semibold gap-1.5 h-9 hover:bg-muted"
                        title="Visualizar atestado em nova aba"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        <span>Visualizar</span>
                      </Button>

                      <Button
                        size="sm"
                        onClick={() => handleDownloadPdf(certificate)}
                        disabled={!hasOfficialPdf}
                        className="rounded-xl text-xs font-bold gap-1.5 h-9 shadow-md shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={
                          hasOfficialPdf
                            ? 'Baixar PDF oficial do Atestado'
                            : 'Este atestado ainda está aguardando a assinatura digital oficial na MeMed'
                        }
                      >
                        <Download className="h-3.5 w-3.5" />
                        <span>Baixar PDF</span>
                      </Button>
                    </div>
                  </div>

                  {/* Informações do Período e Duração */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    <div className="bg-muted/30 rounded-xl p-3.5 border border-border/60 flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                        <Clock className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground block">
                          Período de Afastamento
                        </span>
                        <p className="text-sm font-semibold text-foreground mt-0.5">
                          {formatDate(certificate.startDate)} até{' '}
                          {formatDate(certificate.endDate || certificate.startDate)}
                        </p>
                      </div>
                    </div>

                    <div className="bg-primary/5 rounded-xl p-3.5 border border-primary/20 flex items-center justify-between">
                      <div>
                        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground block">
                          Tempo de Repouso
                        </span>
                        <p className="text-base sm:text-lg font-black text-primary mt-0.5">
                          {certificate.totalDays} {certificate.totalDays === 1 ? 'dia' : 'dias'}
                        </p>
                      </div>
                      <Badge variant="primary" className="font-bold">
                        Válido CLT / RH
                      </Badge>
                    </div>
                  </div>

                  {/* Diagnóstico / Motivo */}
                  {certificate.diagnosis && (
                    <div className="bg-muted/40 rounded-xl p-3.5 sm:p-4 mb-3 border border-border/60">
                      <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground block mb-1">
                        Diagnóstico / Motivo
                      </span>
                      <p className="text-sm font-medium text-foreground leading-relaxed">
                        {certificate.diagnosis}
                      </p>
                    </div>
                  )}

                  {/* Observações adicionais */}
                  {certificate.observations && (
                    <div className="bg-muted/20 rounded-xl p-3 border border-border/40 text-xs text-muted-foreground">
                      <strong className="text-foreground">Observações:</strong> {certificate.observations}
                    </div>
                  )}

                  {/* Rodapé do Card */}
                  <div className="mt-4 pt-3.5 border-t border-border/60 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                      <span>Documento médico legal conforme resoluções CFM</span>
                    </span>

                    <span className="font-mono text-[11px] text-muted-foreground/80">
                      ID: {certificate.id}
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Paginação (Renderizada apenas quando há mais de 1 página) */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded-xl font-medium"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Anterior
          </Button>
          <span className="px-3.5 py-1.5 bg-primary/10 text-primary font-bold text-xs rounded-xl border border-primary/20">
            Página {page} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="rounded-xl font-medium"
          >
            Próximo
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}
